import { useEffect, useState } from "react";
import { fetchLivePlayers } from "@/lib/api";
import { Users } from "lucide-react";

/**
 * Three-row auto-scrolling grid of live Minecraft players.
 * Each row drifts horizontally at a different speed for depth.
 */
export default function LivePlayerGrid() {
  const [data, setData] = useState({ players: [], total: 0, online_servers: 0 });
  useEffect(() => {
    let m = true;
    const load = () => fetchLivePlayers().then((d) => m && setData(d)).catch(() => {});
    load();
    const id = setInterval(load, 25000);
    return () => { m = false; clearInterval(id); };
  }, []);

  const players = data.players || [];
  if (!players.length) {
    return (
      <div className="text-center text-white/40 text-sm py-10">
        No players online right now. Be the first to log in.
      </div>
    );
  }

  // Split players into 3 balanced rows
  const rows = [[], [], []];
  players.forEach((p, i) => rows[i % 3].push(p));

  const speeds = [42, 55, 48]; // seconds per full loop, per row
  const directions = [1, -1, 1];

  return (
    <div data-testid="live-player-grid" className="relative">
      <div className="flex items-center justify-between mb-6 px-4 sm:px-0">
        <div className="flex items-center gap-2 text-sm text-white/60">
          <Users size={16} className="text-[#FFBF00]" />
          <span className="font-mono">
            <span className="text-[#FFBF00] text-lg">{data.total}</span>
            <span className="text-white/40"> players across {data.online_servers} servers</span>
          </span>
        </div>
        <span className="hidden sm:inline text-[10px] uppercase tracking-[0.3em] text-white/40">Live · 25s refresh</span>
      </div>

      <div className="relative overflow-hidden py-2">
        {rows.map((row, ri) => {
          if (!row.length) return null;
          const doubled = [...row, ...row];
          return (
            <div key={ri} className="relative overflow-hidden py-2">
              <div
                className="flex gap-3 w-max"
                style={{
                  animation: `lpg-scroll-${directions[ri] > 0 ? "l" : "r"} ${speeds[ri]}s linear infinite`,
                }}
              >
                {doubled.map((p, i) => (
                  <div
                    key={`${ri}-${i}-${p.username}`}
                    className="group flex items-center gap-3 px-4 py-2.5 rounded-xl glass min-w-[240px] hover:border-[#FFBF00]/40 transition-colors"
                    style={{
                      borderColor: `${p.color}22`,
                      boxShadow: `0 4px 24px ${p.color}12`,
                    }}
                  >
                    <div className="relative">
                      <img
                        src={p.avatar}
                        alt={p.username}
                        className="w-10 h-10 rounded-md"
                        loading="lazy"
                        onError={(e) => (e.target.src = "https://mc-heads.net/avatar/steve/64")}
                      />
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-black pulse-dot" />
                    </div>
                    <div className="leading-tight min-w-0">
                      <div className="text-sm font-semibold truncate">{p.username}</div>
                      <div
                        className="text-[10px] uppercase tracking-widest font-mono truncate"
                        style={{ color: p.color }}
                      >
                        {p.server}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {/* Edge fades */}
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-black to-transparent pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-black to-transparent pointer-events-none" />
      </div>

      <style>{`
        @keyframes lpg-scroll-l { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes lpg-scroll-r { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
      `}</style>
    </div>
  );
}
